// src/pages/TemplateGallery.jsx
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Tabs,
  Tab,
  Box,
  Toolbar,
  Typography,
  Button
} from '@mui/material';
import CloudUploadIcon      from '@mui/icons-material/CloudUpload';
import DescriptionIcon      from '@mui/icons-material/Description';
import { useNavigate }      from 'react-router-dom';

// 1) Contexts
import { useOrganization2 } from '../contexts/OrganizationContext2';
import { useAuth }          from '../contexts/AuthContext';

// 2) Presentation tabs
import OrganizationTemplateTab from '../components/Dialogs/OrganizationTemplateTab';
import AppTemplateTab          from '../components/Dialogs/AppTemplateTab';

// 3) Data hooks
import { useFetchOrgTemplates    } from '../hooks/useFetchOrgTemplates';
import { useFetchGlobalTemplates } from '../hooks/useFetchGlobalTemplates';

// 4) Helper
import { resolveScreenshotUrlsForForms } from '../utils/formThumbnailResolver';

// 5) Dialog
import SubmitTemplateDialog from '../components/Dialogs/SubmitTemplateDialog';

export default function TemplateGallery() {
//  console.log('[TemplateGallery] Component rendering…');

  // ─────────────────────────────────────────────────────────
  // Optimistic‑refresh ticks (bumped after a publish)
  // ─────────────────────────────────────────────────────────
  const [orgRefreshTick,    setOrgRefreshTick]    = useState(0);
  const [globalRefreshTick, setGlobalRefreshTick] = useState(0);

  // 1) Organization context
  const { organization }  = useOrganization2();
  const orgId             = organization?.id   || null;
  const orgName           = organization?.name || 'My Organization';

//  console.log('[TemplateGallery] organization context:', {
//    orgId,
//    orgName,
//    organizationObj: organization
//  });

  // 2) Local tab state
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_e, newValue) => {
//    console.log('[TemplateGallery] handleTabChange', { from: tabIndex, to: newValue });
    setTabIndex(newValue);
  };

  // 3) Submit‑dialog visibility
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  // 4) Permissions
  const { hasPermission }          = useAuth();
  const canPublishOrgTemplates     = hasPermission('publishOrganizationTemplates');
  const canPublishGlobalTemplates  = hasPermission('publishGlobalTemplates');
//  console.log('[TemplateGallery] user permissions:', { canPublishOrgTemplates, canPublishGlobalTemplates });

  // ─────────────────────────────────────────────────────────
  // ORG templates  (pass orgRefreshTick as refreshKey)
  // ─────────────────────────────────────────────────────────
  const {
    orgTemplates : rawOrgTemplates,
    isLoading    : orgIsLoading,
    hasMore      : orgHasMore,
    fetchNextBatch: fetchMoreOrg
  } = useFetchOrgTemplates(20, orgRefreshTick);

  const [enrichedOrgTemplates, setEnrichedOrgTemplates] = useState([]);
  const [enrichIsLoading,      setEnrichIsLoading]      = useState(false);

  useEffect(() => {
//    console.log('[TemplateGallery] ORG enrich effect', { rawLen: rawOrgTemplates.length, orgId });
    if (!rawOrgTemplates.length) {
      setEnrichedOrgTemplates([]);
      setEnrichIsLoading(false);
      return;
    }
    if (!orgId) {
      setEnrichedOrgTemplates(rawOrgTemplates);
      setEnrichIsLoading(false);
      return;
    }
    (async () => {
      setEnrichIsLoading(true);
      try {
        const withShots = await resolveScreenshotUrlsForForms(rawOrgTemplates, orgId);
        setEnrichedOrgTemplates(withShots);
      } catch (err) {
        console.error('[TemplateGallery] ORG screenshot error → fallback to raw', err);
        setEnrichedOrgTemplates(rawOrgTemplates);
      } finally {
        setEnrichIsLoading(false);
      }
    })();
  }, [rawOrgTemplates, orgId]);

  const combinedIsLoading = orgIsLoading || enrichIsLoading;

  // ─────────────────────────────────────────────────────────
  // GLOBAL templates  (pass globalRefreshTick as refreshKey)
  // ─────────────────────────────────────────────────────────
  const {
    globalTemplates: rawGlobalTemplates,
    isLoading      : globalIsLoading,
    hasMore        : globalHasMore,
    fetchNextBatch : fetchMoreGlobal
  } = useFetchGlobalTemplates(20, globalRefreshTick);

  const [enrichedGlobalTemplates, setEnrichedGlobalTemplates] = useState([]);
  const [enrichGlobalIsLoading ,  setEnrichGlobalIsLoading ]   = useState(false);

  useEffect(() => {
//    console.log('[TemplateGallery] GLOBAL enrich effect', { rawLen: rawGlobalTemplates.length });
    if (!rawGlobalTemplates.length) {
      setEnrichedGlobalTemplates([]);
      setEnrichGlobalIsLoading(false);
      return;
    }
    (async () => {
      setEnrichGlobalIsLoading(true);
      try {
      const withShots = await resolveScreenshotUrlsForForms(rawGlobalTemplates, 'global');
        setEnrichedGlobalTemplates(withShots);
      } catch (err) {
        console.error('[TemplateGallery] GLOBAL screenshot error → fallback to raw', err);
        setEnrichedGlobalTemplates(rawGlobalTemplates);
      } finally {
        setEnrichGlobalIsLoading(false);
      }
    })();
  }, [rawGlobalTemplates]);

  const combinedGlobalLoading = globalIsLoading || enrichGlobalIsLoading;

  // ─────────────────────────────────────────────────────────
  // Publish‑success handler → bump the appropriate tick
  // ─────────────────────────────────────────────────────────
  const handlePublishSuccess = ({ scope }) => {
//    console.log('[TemplateGallery] handlePublishSuccess', scope);
    if (scope === 'organization') {
      setOrgRefreshTick(t => t + 1);
    } else {
      setGlobalRefreshTick(t => t + 1);
    }
  };

 // ─────────────────────────────────────────────────────────
 //  callback after a template is removed from the gallery
 // ─────────────────────────────────────────────────────────
  const handleUnpublishSuccess = () => {
//    console.log('[TemplateGallery] Template un‑published → refreshing list');
    setGlobalRefreshTick(t => t + 1);    // bump the same counter we use elsewhere
  };


  // ========================
  // 5) Render the page
  // ========================
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#e8eaed',
        margin: 0,
        padding: 0,
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: '#fff',
          color: 'text.primary',
          boxShadow: 'none',
          margin: 0,
          padding: 0,
          borderBottom: 'none'
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            minHeight: 48,
            margin: 0,
            padding: 0,
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: '100%',
              pl: '10%',   // 10% left padding
            }}
          >
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight: 32,
                margin: 0,
                padding: 0,
              }}
            >
              <Tab
                label={orgName}
                sx={{
                  minWidth: 80,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  minHeight: 32,
                  padding: '0px 12px',
                  margin: 0,
                }}
              />
              <Tab
                label="General"
                sx={{
                  minWidth: 80,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  minHeight: 32,
                  padding: '0px 12px',
                  margin: 0,
                }}
              />
            </Tabs>
          </Box>
        </Toolbar>
      </AppBar>

      {console.log('[TemplateGallery] Current tabIndex:', tabIndex)}

      {/* 
        Tab 0 => Org-level templates 
        Tab 1 => General (global) templates
      */}

      {/* ================================
          (1) Organization Templates Tab
      ================================ */}
      {tabIndex === 0 && (
        <>
          {console.log(
            '[TemplateGallery] Rendering OrganizationTemplateTab with enrichedOrgTemplates length:',
            enrichedOrgTemplates.length
          )}
          <OrganizationTemplateTab
            // Provide everything the tab needs to show & infinite-scroll the org templates
            orgTemplates={enrichedOrgTemplates}
            isLoading={combinedIsLoading}
            hasMore={orgHasMore}
            fetchNextBatch={fetchMoreOrg}
            canPublish={canPublishOrgTemplates}
            onUnpublishSuccess={handleUnpublishSuccess}
            onSubmitTemplateClick={() => {
//              console.log('[TemplateGallery] onSubmitTemplateClick called (org scope).');
              setShowSubmitDialog(true);
            }}
          />
        </>
      )}

      {/* ================================
          (2) General (Global) Templates Tab
      ================================ */}
      {tabIndex === 1 && (
        <>
          {console.log('[TemplateGallery] Rendering General (Global) Templates tab')}
          <AppTemplateTab
            globalTemplates={enrichedGlobalTemplates}
            isLoading={combinedGlobalLoading}
            hasMore={globalHasMore}
            fetchNextBatch={fetchMoreGlobal}
            canPublish={canPublishGlobalTemplates}
            onUnpublishSuccess={handleUnpublishSuccess}
            onSubmitTemplateClick={() => {
              console.log('[TemplateGallery] onSubmitTemplateClick called (global scope).');
              setShowSubmitDialog(true);
            }}
          />
        </>
      )}

      {/* ================================
          SubmitTemplateDialog (shared)
      ================================ */}
      {console.log(
        '[TemplateGallery] SubmitTemplateDialog open:', 
        showSubmitDialog, 
        'scope:', 
        tabIndex === 0 ? 'organization' : 'global'
      )}
      <SubmitTemplateDialog
        open={showSubmitDialog}
        onClose={() => {
//          console.log('[TemplateGallery] SubmitTemplateDialog closed');
          setShowSubmitDialog(false);
        }}
        templateScope={tabIndex === 0 ? 'organization' : 'global'}
        orgId={tabIndex === 0 ? orgId : ''}
        onPublishSuccess={handlePublishSuccess} 
      />
    </Box>
  );
}
